import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Card, Descriptions, Input, Space, Typography } from 'antd';
import { getMemberCourseProcessing } from '@/services/technical';
import { processingTime } from './CommunicationProcessingPanel';
import FactSyncProcessingSummary, {
  validateFactSync,
} from './FactSyncProcessingSummary';
import CourseEvaluationRetryButton from './CourseEvaluationRetryButton';
import styles from './SimpleWorkspace.less';

const TASK_LABELS = {
  pending: '等待执行',
  running: '执行中',
  idle: '空闲',
  succeeded: '成功',
  failed: '失败',
  unknown: '未知状态',
};
const RESULT_LABELS = {
  not_generated: '尚未生成',
  generating: '生成中',
  succeeded: '成功',
  failed: '失败',
  unknown: '未知状态',
};
const validTime = (v) =>
  v == null || (Number.isSafeInteger(v) && v > 0 && v <= 8640000000000000);
const version = (v) => v == null || (Number.isSafeInteger(v) && v >= 0);
const ref = (v) =>
  v == null || (typeof v === 'string' && !!v.trim() && v.length <= 64);
export function readCourseProcessing(response, memberId, roomId) {
  const d = response?.data;
  if (
    !['00000', '0'].includes(String(response?.code)) ||
    String(d?.memberId) !== String(memberId) ||
    d?.roomId !== roomId ||
    typeof d?.available !== 'boolean' ||
    d.queriedAt == null ||
    !validTime(d.queriedAt)
  )
    throw new Error('Invalid scope');
  validateFactSync(d.factSyncTasks, d.factSyncHasMore);
  if (!d.available) return d;
  if (
    !Object.prototype.hasOwnProperty.call(TASK_LABELS, d.taskStatus) ||
    !Object.prototype.hasOwnProperty.call(RESULT_LABELS, d.observationStatus) ||
    !Object.prototype.hasOwnProperty.call(RESULT_LABELS, d.assessmentStatus) ||
    ![d.dirtyVersion, d.processedVersion, d.claimedVersion].every(version) ||
    (d.attemptCount != null &&
      (!Number.isInteger(d.attemptCount) ||
        d.attemptCount < 0 ||
        d.attemptCount > 2147483647)) ||
    ![d.nextExecuteAt, d.startedAt, d.updatedAt, d.assessmentGeneratedAt].every(
      validTime,
    ) ||
    !ref(d.evaluationId) ||
    !ref(d.assessmentId) ||
    (d.evaluationId == null) !== (d.observationStatus === 'not_generated') ||
    (d.assessmentId == null) !== (d.assessmentStatus === 'not_generated') ||
    (d.assessmentId != null && d.evaluationId == null)
  )
    throw new Error('Invalid status');
  return d;
}
export default function CourseProcessingPanel({ memberId }) {
  const identity = String(memberId);
  const [roomText, setRoomText] = useState('');
  const [state, setState] = useState({});
  const generation = useRef(0);
  const inFlight = useRef(false);
  useEffect(() => {
    generation.current += 1;
    inFlight.current = false;
    setState({});
    setRoomText('');
    return () => {
      generation.current += 1;
      inFlight.current = false;
    };
  }, [identity]);
  const current = state.identity === identity ? state : {};
  const load = async () => {
    if (inFlight.current) return;
    const roomId = Number(roomText.trim());
    if (!/^[1-9]\d*$/.test(roomText.trim()) || !Number.isSafeInteger(roomId)) {
      setState({ identity, error: '请输入有效的正整数课程 ID' });
      return;
    }
    inFlight.current = true;
    const request = ++generation.current;
    setState({ identity, loading: true });
    try {
      const response = await getMemberCourseProcessing({
        requestData: { memberId, roomId },
      });
      const data = readCourseProcessing(response, memberId, roomId);
      if (request === generation.current) setState({ identity, data });
    } catch (error) {
      if (request === generation.current)
        setState({
          identity,
          error:
            { 401: '请先登录后台', 403: '无会员档案菜单权限' }[
              error?.response?.status
            ] || '课程评估状态暂时无法加载，请重新查询',
        });
    } finally {
      if (request === generation.current) inFlight.current = false;
    }
  };
  const data = current.data;
  const unprocessed =
    data?.dirtyVersion != null &&
    data?.processedVersion != null &&
    data.dirtyVersion > data.processedVersion;
  return (
    <Card
      size="small"
      bordered={false}
      title="课程同步与评估"
      className={`${styles.infoCard} ${styles.processingCard}`}
    >
      <Typography.Paragraph type="secondary" className={styles.processingIntro}>
        按课程 ID 查看训练事实同步、课程结构和智能结论三个阶段。
      </Typography.Paragraph>
      <details className={styles.processingDetails}>
        <summary>查看查询边界</summary>
        <Typography.Paragraph type="secondary">
          本操作只读取指定课程状态，不触发评估、模型调用或任务重试，也不是全部课程的健康汇总。
        </Typography.Paragraph>
      </details>
      <Space wrap>
        <Input
          className={styles.courseProcessingInput}
          aria-label="查询评估状态的课程 ID"
          inputMode="numeric"
          placeholder="输入课程 ID"
          value={roomText}
          disabled={current.loading}
          onChange={(event) => {
            setRoomText(event.target.value);
            generation.current += 1;
            inFlight.current = false;
            setState({});
          }}
        />
        <Button
          onClick={load}
          loading={current.loading}
          disabled={current.loading}
        >
          {current.loading ? '查询中…' : '查询课程状态'}
        </Button>
      </Space>
      {current.error && (
        <Typography.Paragraph type="warning">
          {current.error}
        </Typography.Paragraph>
      )}
      {data && (
        <Typography.Paragraph type="secondary">
          课程 {data.roomId} · 查询时间（UTC+8）：
          {processingTime(data.queriedAt)}
        </Typography.Paragraph>
      )}
      {data && !data.available && (
        <Typography.Paragraph>
          未查到当前会员与课程匹配的评估任务，可能尚未入队；不代表课程不存在。
        </Typography.Paragraph>
      )}
      {data && (
        <FactSyncProcessingSummary
          memberId={data.memberId}
          roomId={data.roomId}
          tasks={data.factSyncTasks}
          hasMore={data.factSyncHasMore}
        />
      )}
      {data?.available && (
        <>
          <CourseEvaluationRetryButton
            key={`${data.memberId}:${data.roomId}:${data.dirtyVersion}:${data.updatedAt}:${data.attemptCount}`}
            task={data}
            memberId={data.memberId}
            roomId={data.roomId}
          />
          {unprocessed && (
            <Typography.Paragraph type="warning">
              仍有未处理的新证据；下方已有的成功结果不能视为本轮已经完成。
            </Typography.Paragraph>
          )}
          {data.observationStatus === 'succeeded' &&
            data.assessmentStatus !== 'succeeded' && (
              <Typography.Paragraph type="warning">
                课程结构已生成，智能结论尚未成功。
              </Typography.Paragraph>
            )}
          <Descriptions size="small" column={{ xs: 1, sm: 2, lg: 3 }}>
            <Descriptions.Item label="任务调度状态">
              {TASK_LABELS[data.taskStatus]}
            </Descriptions.Item>
            <Descriptions.Item label="课程结构（最新快照）">
              {RESULT_LABELS[data.observationStatus]}
            </Descriptions.Item>
            <Descriptions.Item label="智能结论（该快照·当前协议）">
              {RESULT_LABELS[data.assessmentStatus]}
            </Descriptions.Item>
            <Descriptions.Item label="当前轮尝试次数">
              {data.attemptCount ?? '未提供'}
            </Descriptions.Item>
            <Descriptions.Item label="最新证据版本">
              {data.dirtyVersion ?? '未提供'}
            </Descriptions.Item>
            <Descriptions.Item label="已处理版本">
              {data.processedVersion ?? '未提供'}
            </Descriptions.Item>
            <Descriptions.Item label="当前领取版本">
              {data.claimedVersion ?? '未提供'}
            </Descriptions.Item>
            <Descriptions.Item label="计划执行时间（UTC+8）">
              {processingTime(data.nextExecuteAt)}
            </Descriptions.Item>
            <Descriptions.Item label="本次领取时间（UTC+8）">
              {processingTime(data.startedAt)}
            </Descriptions.Item>
            <Descriptions.Item label="任务更新时间（UTC+8）">
              {processingTime(data.updatedAt)}
            </Descriptions.Item>
            <Descriptions.Item label="结论生成时间（UTC+8）">
              {processingTime(data.assessmentGeneratedAt)}
            </Descriptions.Item>
            <Descriptions.Item label="课程快照 ID">
              {data.evaluationId || '未生成'}
            </Descriptions.Item>
            <Descriptions.Item label="智能结论 ID">
              {data.assessmentId || '未生成'}
            </Descriptions.Item>
          </Descriptions>
          <Typography.Paragraph type="secondary">
            结论仅查询当前协议及提示词版本。版本字段缺失时无法判断是否追上新证据；计划执行时间不保证准时执行。尝试次数可能随新一轮或成功处理重置；失败是否可自动重试需查任务配置和日志，本页不做承诺。
          </Typography.Paragraph>
        </>
      )}
    </Card>
  );
}
CourseProcessingPanel.propTypes = {
  memberId: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    .isRequired,
};
